import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/expenses/new")({
  component: NewExpense,
});

const CATEGORIES = [
  "materials",
  "labor",
  "equipment",
  "fuel",
  "travel",
  "meals",
  "permits",
  "subcontractor",
  "office",
  "bdi",
  "other",
] as const;

const PAYMENT_METHODS = ["cash", "card", "check", "bank_transfer", "other"] as const;

type JobSiteOpt = { id: string; name: string };
type ProjectOpt = { id: string; name: string; code: string };

function NewExpense() {
  const { t } = useTranslation();
  const { company, user } = useAuth();
  const dragCounter = useRef(0);
  const navigate = useNavigate();
  const [jobSites, setJobSites] = useState<JobSiteOpt[]>([]);
  const [projects, setProjects] = useState<ProjectOpt[]>([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    project_id: "",
    job_site_id: "",
    category: "materials",
    vendor: "",
    description: "",
    amount: "",
    expense_date: new Date().toISOString().slice(0, 10),
    payment_method: "card",
    reimbursable: false,
    notes: "",
  });
  const [receipt, setReceipt] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      const [{ data: js }, { data: ps }] = await Promise.all([
        supabase
          .from("job_sites")
          .select("id, name")
          .eq("company_id", company.id)
          .eq("is_active", true)
          .order("name"),
        supabase
          .from("projects")
          .select("id, name, code")
          .eq("company_id", company.id)
          .order("name"),
      ]);
      setJobSites((js ?? []) as JobSiteOpt[]);
      setProjects((ps ?? []) as ProjectOpt[]);
    })();
  }, [company?.id]);

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!company?.id || !user?.id) return;
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(t("newExpense.amountInvalid"));
      return;
    }
    setSaving(true);
    const { data: inserted, error } = await supabase
      .from("expenses")
      .insert({
        number: "",
        company_id: company.id,
        created_by: user.id,
        project_id: form.project_id || null,
        job_site_id: form.job_site_id || null,
        category: form.category,
        vendor: form.vendor || null,
        description: form.description || null,
        amount,
        currency: company.currency ?? "USD",
        expense_date: form.expense_date,
        payment_method: form.payment_method || null,
        reimbursable: form.reimbursable,
        notes: form.notes || null,
      })
      .select("id")
      .single();

    if (error || !inserted) {
      toast.error(error?.message ?? t("newExpense.createFailed"));
      setSaving(false);
      return;
    }

    if (receipt) {
      const ext = receipt.name.split(".").pop() ?? "bin";
      const path = `${company.id}/${inserted.id}/receipt-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("receipts")
        .upload(path, receipt, { upsert: true, contentType: receipt.type });
      if (upErr) {
        toast.warning(t("newExpense.receiptFailed", { msg: upErr.message }));
      } else {
        await supabase.from("expenses").update({ receipt_path: path }).eq("id", inserted.id);
      }
    }

    toast.success(t("newExpense.createdToast"));
    navigate({ to: "/expenses/$expenseId", params: { expenseId: inserted.id } });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/expenses">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("newExpense.back")}
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("newExpense.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("newExpense.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("newExpense.details")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("newExpense.project")}</Label>
              <Select
                value={form.project_id || "none"}
                onValueChange={(v) => update("project_id", v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("newExpense.selectProject")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("newExpense.noProject")}</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("newExpense.jobSite")}</Label>
              <Select
                value={form.job_site_id || "none"}
                onValueChange={(v) => update("job_site_id", v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("newExpense.selectSite")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("newExpense.noSite")}</SelectItem>
                  {jobSites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("newExpense.category")}</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {t(`newExpense.categories.${c}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("newExpense.paymentMethod")}</Label>
              <Select
                value={form.payment_method}
                onValueChange={(v) => update("payment_method", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(`newExpense.paymentMethods.${m}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">{t("newExpense.vendor")}</Label>
              <Input
                id="vendor"
                value={form.vendor}
                onChange={(e) => update("vendor", e.target.value)}
                placeholder={t("newExpense.vendorPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">{t("newExpense.amount")}</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => update("amount", e.target.value)}
                placeholder="0.00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expense_date">{t("newExpense.date")}</Label>
              <Input
                id="expense_date"
                type="date"
                value={form.expense_date}
                onChange={(e) => update("expense_date", e.target.value)}
                required
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-input px-3 py-2">
              <div>
                <Label htmlFor="reimbursable" className="cursor-pointer">
                  {t("newExpense.reimbursable")}
                </Label>
                <p className="text-xs text-muted-foreground">{t("newExpense.reimbursableHint")}</p>
              </div>
              <Switch
                id="reimbursable"
                checked={form.reimbursable}
                onCheckedChange={(v) => update("reimbursable", v)}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="description">{t("newExpense.description")}</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder={t("newExpense.descriptionPlaceholder")}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">{t("newExpense.notes")}</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("newExpense.receipt")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                dragCounter.current += 1;
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                dragCounter.current -= 1;
                if (dragCounter.current <= 0) {
                  setDragOver(false);
                  dragCounter.current = 0;
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                dragCounter.current = 0;
                setDragOver(false);
                const file = e.dataTransfer.files?.[0] ?? null;
                if (!file) return;
                const name = file.name.toLowerCase();
                const okType =
                  file.type.startsWith("image/") ||
                  file.type === "application/pdf" ||
                  /\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?|pdf)$/.test(name);
                if (okType) {
                  setReceipt(file);
                } else {
                  toast.error(t("newExpense.invalidFileType"));
                }
              }}
            >
              <Label
                htmlFor="receipt"
                className={
                  "flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-4 text-sm transition-colors " +
                  (dragOver
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-input text-muted-foreground hover:bg-muted/50")
                }
              >
                <Upload className="h-4 w-4" />
                {receipt ? receipt.name : t("newExpense.attachHint")}
              </Label>
            </div>
            <Input
              id="receipt"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/expenses">{t("newExpense.cancel")}</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? t("newExpense.saving") : t("newExpense.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}
